// ProRecordSale.jsx
// Route: /pro/record-sale/:bookingId/:source

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from './axiosConfig';
import {
  Box, Paper, Typography, Button, TextField, MenuItem,
  Chip, IconButton, Alert, CircularProgress,
  Divider, Grid, Snackbar, Dialog, DialogTitle,
  DialogContent, DialogActions, Stack, Tooltip,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Check as CheckIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  EventRepeat as EventRepeatIcon,
  CalendarMonth as CalendarMonthIcon,
  ContentCut as ContentCutIcon,
  Person as PersonIcon,
  PersonAdd as PersonAddIcon,
  Close as CloseIcon,
  SwapHoriz as SwapHorizIcon,
  Warning as WarningIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import {
  PatchTestChip,
  ClientSearchField,
  NewClientDialog,
} from './ProClientComponents';

const NAVY  = '#2f3542';
const GREEN = '#2e7d32';
const fmt   = (n) => parseFloat(n || 0).toFixed(2);

const addWeeks = (isoDate, weeks) => {
  const d = new Date(isoDate + 'T00:00:00');
  d.setDate(d.getDate() + weeks * 7);
  return d.toISOString().split('T')[0];
};

const fmtDisplayDate = (iso) =>
  new Date(iso + 'T00:00:00').toLocaleDateString('en-GB', {
    weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
  });

const WEEK_OPTIONS = [4, 5, 6, 7, 8];

// ── Rebook Dialog ─────────────────────────────────────────────────────────

function RebookDialog({ open, onClose, booking, lineItems, weeksAhead, onBooked }) {
  const suggestedDate = booking ? addWeeks(booking.appointment_date, weeksAhead) : '';
  const [date, setDate]     = useState(suggestedDate);
  const [time, setTime]     = useState(booking?.start_time?.slice(0, 5) || '09:00');
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState(null);

  const [selectedClient,  setSelectedClient]  = useState(null);
  const [changingClient,  setChangingClient]  = useState(false);
  const [newClientOpen,   setNewClientOpen]   = useState(false);

  useEffect(() => {
    if (open && booking) {
      setDate(addWeeks(booking.appointment_date, weeksAhead));
      setTime(booking.start_time?.slice(0, 5) || '09:00');
      setError(null);
      setChangingClient(false);
      if (booking.client_id) {
        setSelectedClient({ id: booking.client_id, full_name: booking.client_name, phone: booking.client_phone || '' });
      } else {
        setSelectedClient(null);
      }
    }
  }, [open, booking, weeksAhead]);

  const handleClientSelected   = (client) => { setSelectedClient(client); setChangingClient(false); };
  const handleNewClientCreated = (client) => { setSelectedClient(client); setChangingClient(false); };
  const handleClearClient      = ()       => { setSelectedClient(null); setChangingClient(true); };

  const serviceItems = lineItems.filter(i => i.type === 'service');

  const handleConfirm = async () => {
    if (!date || !time) return;
    setSaving(true);
    setError(null);
    try {
      const serviceIds = (booking.services || []).map(s => s.service_id).filter(Boolean);
      await api.post('/pro-booking/bookings/manual', {
        employee_id:      booking.employee_id,
        service_ids:      serviceIds.length ? serviceIds : undefined,
        appointment_date: date,
        start_time:       time,
        client_name:      selectedClient?.full_name  || booking.client_name,
        client_phone:     selectedClient?.phone       || booking.client_phone,
        client_email:     selectedClient?.email       || booking.client_email || undefined,
        client_id:        selectedClient?.id          || undefined,
        notes:            `Rebooked from ${booking.appointment_date}`,
      });
      onBooked(date);
    } catch (e) {
      setError(e.response?.data?.error || 'Could not create booking. Please check the diary.');
    } finally {
      setSaving(false);
    }
  };

  if (!booking) return null;

  const displayName  = selectedClient?.full_name || booking.client_name;
  const displayPhone = selectedClient?.phone      || booking.client_phone;

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EventRepeatIcon sx={{ color: NAVY }} />
            Rebook Appointment
          </Box>
        </DialogTitle>

        <DialogContent>
          <Box sx={{ bgcolor: 'grey.50', borderRadius: 1, p: 1.5, mb: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
              <ContentCutIcon fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary">{booking.stylist_name}</Typography>
            </Box>
            {serviceItems.map((item, i) => (
              <Typography key={i} variant="caption" color="text.secondary" sx={{ display: 'block', pl: 3.5 }}>
                {item.name}
              </Typography>
            ))}

            <Divider sx={{ my: 1.25 }} />

            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <PersonIcon fontSize="small" color="action" />
                <Box>
                  <Typography variant="body2" fontWeight={600}>{displayName}</Typography>
                  {displayPhone && (
                    <Typography variant="caption" color="text.secondary">{displayPhone}</Typography>
                  )}
                  {selectedClient?.patch_test?.status && (
                    <Box sx={{ mt: 0.5 }}>
                      <PatchTestChip status={selectedClient.patch_test.status} />
                    </Box>
                  )}
                  {selectedClient?.allergies && (
                    <Typography variant="caption" sx={{ display: 'block', color: '#c62828', fontWeight: 600, mt: 0.25 }}>
                      ⚠ {selectedClient.allergies}
                    </Typography>
                  )}
                </Box>
              </Box>
              <Tooltip title="Change client">
                <IconButton size="small" onClick={() => setChangingClient(v => !v)}>
                  <SwapHorizIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>

            {changingClient && (
              <Box sx={{ mt: 1.5 }}>
                <ClientSearchField onClientSelected={handleClientSelected} />
                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                  <Button size="small" variant="outlined" startIcon={<PersonAddIcon />}
                    onClick={() => setNewClientOpen(true)} sx={{ fontSize: '0.75rem' }}>
                    New client
                  </Button>
                  {selectedClient && (
                    <Button size="small" color="inherit" startIcon={<CloseIcon fontSize="small" />}
                      onClick={handleClearClient} sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                      Clear
                    </Button>
                  )}
                  <Button size="small" color="inherit" onClick={() => setChangingClient(false)}
                    sx={{ ml: 'auto', fontSize: '0.75rem', color: 'text.secondary' }}>
                    Cancel
                  </Button>
                </Box>
              </Box>
            )}
          </Box>

          <Stack spacing={2}>
            <TextField label="Date" type="date" value={date} onChange={e => setDate(e.target.value)}
              size="small" fullWidth InputLabelProps={{ shrink: true }}
              inputProps={{ min: addWeeks(booking.appointment_date, 1) }} />
            <TextField label="Time" type="time" value={time} onChange={e => setTime(e.target.value)}
              size="small" fullWidth InputLabelProps={{ shrink: true }} />
          </Stack>

          {date && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              {fmtDisplayDate(date)} at {time}
            </Typography>
          )}

          {error && <Alert severity="warning" sx={{ mt: 2, fontSize: '12px' }}>{error}</Alert>}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose} color="inherit" disabled={saving}>Cancel</Button>
          <Button onClick={handleConfirm} variant="contained" disabled={saving || !date || !time}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <CheckIcon />}
            sx={{ bgcolor: NAVY, '&:hover': { bgcolor: '#1e2530' } }}>
            {saving ? 'Booking…' : 'Confirm Rebook'}
          </Button>
        </DialogActions>
      </Dialog>

      <NewClientDialog
        open={newClientOpen}
        onClose={() => setNewClientOpen(false)}
        onCreated={handleNewClientCreated}
      />
    </>
  );
}

// ── Main component ────────────────────────────────────────────────────────

export default function ProRecordSale() {
  const { bookingId, source } = useParams();
  const navigate = useNavigate();

  const [loading,          setLoading]          = useState(true);
  const [saving,           setSaving]           = useState(false);
  const [error,            setError]            = useState(null);
  const [snackbar,         setSnackbar]         = useState({ open: false, message: '', severity: 'success' });

  const [booking,          setBooking]          = useState(null);
  const [lineItems,        setLineItems]        = useState([]);
  const [retailProducts,   setRetailProducts]   = useState([]);
  const [selectedRetailId, setSelectedRetailId] = useState('');

  const [discount,         setDiscount]         = useState('0');
  const [tip,              setTip]              = useState('0');
  const [paymentMethod,    setPaymentMethod]    = useState('Cash');
  const [splitRows,        setSplitRows]        = useState([
    { method: 'Cash', amount: '' },
    { method: 'Card', amount: '' },
  ]);
  const [vatRegistered,    setVatRegistered]    = useState(false);

  const [saleComplete,     setSaleComplete]     = useState(false);
  const [rebookOpen,       setRebookOpen]       = useState(false);
  const [rebookWeeks,      setRebookWeeks]      = useState(null);
  const [rebookDone,       setRebookDone]       = useState(false);
  const [rebookDate,       setRebookDate]       = useState(null);
  const [weekAvailability, setWeekAvailability] = useState({});

  const recommendedWeeks = booking?.services?.[0]?.recommended_interval_weeks || null;

  const loadBooking = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`/pro-diary/bookings/${bookingId}`, {
        params: { source: source || 'online' },
      });
      const b = res.data;
      setBooking(b);
      setLineItems((b.services || []).map((svc, i) => ({
        id:            `svc-${svc.service_id || i}`,
        type:          'service',
        name:          svc.service_name,
        duration:      svc.duration,
        price:         parseFloat(svc.price || 0),
        price_locked:  svc.price_locked || false,
        locked_price:  svc.locked_price || null,
        current_price: svc.current_price || null,
      })));
    } catch {
      setError('Could not load booking. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [bookingId, source]);

  const loadRetailProducts = useCallback(async () => {
    try {
      const res = await api.get('/solo/products');
      const all = res.data.products || [];
      setRetailProducts(all.filter(p => p.is_retail !== false));
    } catch { /* non-fatal */ }
  }, []);

  const loadSettings = useCallback(async () => {
    try {
      const res = await api.get('/solo/settings');
      setVatRegistered(res.data?.vat_registered || false);
    } catch { /* defaults to false */ }
  }, []);

  useEffect(() => {
    loadBooking();
    loadRetailProducts();
    loadSettings();
  }, [loadBooking, loadRetailProducts, loadSettings]);

  useEffect(() => {
    if (!saleComplete || !booking) return;
    const checkWeek = async (weeks) => {
      setWeekAvailability(prev => ({ ...prev, [weeks]: 'checking' }));
      try {
        const targetDate   = addWeeks(booking.appointment_date, weeks);
        const firstService = booking.services?.[0];
        if (!firstService?.service_id) {
          setWeekAvailability(prev => ({ ...prev, [weeks]: 'available' }));
          return;
        }
        const res = await api.get('/pro-booking/availability', {
          params: { service_id: firstService.service_id, date: targetDate, stylist_id: booking.employee_id },
        });
        const slots = res.data?.available_stylists?.[0]?.available_slots || [];
        if (slots.length === 0)     setWeekAvailability(prev => ({ ...prev, [weeks]: 'unavailable' }));
        else if (slots.length <= 3) setWeekAvailability(prev => ({ ...prev, [weeks]: 'limited' }));
        else                        setWeekAvailability(prev => ({ ...prev, [weeks]: 'available' }));
      } catch {
        setWeekAvailability(prev => ({ ...prev, [weeks]: 'available' }));
      }
    };
    WEEK_OPTIONS.forEach(w => checkWeek(w));
  }, [saleComplete, booking]);

  const subtotal       = lineItems.reduce((sum, i) => sum + (parseFloat(i.price) || 0), 0);
  const discountAmt    = parseFloat(discount) || 0;
  const tipAmt         = parseFloat(tip) || 0;
  const dueToday       = Math.max(0, subtotal - discountAmt + tipAmt);
  const vatAmount      = vatRegistered && dueToday > 0 ? dueToday / 6 : 0;
  const splitTotal     = splitRows.reduce((sum, r) => sum + (parseFloat(r.amount) || 0), 0);
  const splitRemaining = Math.max(0, dueToday - splitTotal);

  const updatePrice    = (id, value) => setLineItems(prev => prev.map(i => i.id === id ? { ...i, price: value } : i));
  const removeItem     = (id)        => setLineItems(prev => prev.filter(i => i.id !== id));

  const addRetailProduct = () => {
    const product = retailProducts.find(p => p.id === parseInt(selectedRetailId));
    if (!product) return;
    setLineItems(prev => [...prev, {
      id: `retail-${product.id}-${Date.now()}`, type: 'retail', name: product.name,
      price: parseFloat(product.retail_price || product.price || 0),
    }]);
    setSelectedRetailId('');
  };

  const updateSplitRow = (idx, field, value) => setSplitRows(prev => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r));
  const addSplitRow    = () => setSplitRows(prev => [...prev, { method: 'Cash', amount: '' }]);
  const removeSplitRow = (idx) => setSplitRows(prev => prev.filter((_, i) => i !== idx));

  const handleCompleteSale = async () => {
    if (lineItems.length === 0) { setError('Add at least one service or product before completing the sale.'); return; }
    setSaving(true);
    setError(null);
    try {
      await api.post(`/pro-diary/bookings/${bookingId}/checkout`, {
        source:         source || 'online',
        line_items:     lineItems.map(i => ({ name: i.name, type: i.type, price: parseFloat(i.price) })),
        payment_method: paymentMethod,
        discount:       discountAmt,
        tip:            tipAmt,
        vat_registered: vatRegistered,
        split_payments: paymentMethod === 'Split' ? splitRows : [],
      });
      setSaleComplete(true);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to record sale. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleNoShow = async () => {
    setSaving(true);
    try {
      await api.post(`/pro-diary/bookings/${bookingId}/cancel`, { source, reason: 'no_show' });
      setSnackbar({ open: true, message: 'Marked as no-show.', severity: 'info' });
      setTimeout(() => navigate('/pro/diary'), 1500);
    } catch {
      setError('Failed to mark as no-show.');
    } finally {
      setSaving(false);
    }
  };

  const handleWeekClick       = (weeks) => { setRebookWeeks(weeks); setRebookOpen(true); };
  const handleRebookConfirmed = (confirmedDate) => { setRebookOpen(false); setRebookDone(true); setRebookDate(confirmedDate); };

  const weekButtonStyle = (weeks) => {
    const isRecommended = recommendedWeeks === weeks;
    const avail = weekAvailability[weeks];
    const base = { flex: '1 1 80px', py: 1.5, flexDirection: 'column', position: 'relative' };
    if (isRecommended) return { ...base, borderColor: NAVY, borderWidth: 2, color: NAVY, bgcolor: '#f0f4ff', '&:hover': { bgcolor: '#e8eeff', borderColor: NAVY } };
    if (avail === 'unavailable') return { ...base, borderColor: '#e0e0e0', color: '#bdbdbd', '&:hover': { borderColor: '#bdbdbd', bgcolor: '#fafafa' } };
    return { ...base, borderColor: '#c0c0c0', color: NAVY, '&:hover': { borderColor: NAVY, bgcolor: '#f5f5f5' } };
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // ── Post-sale ─────────────────────────────────────────────────────────
  if (saleComplete) {
    return (
      <Box sx={{ maxWidth: 640, mx: 'auto', p: { xs: 2, md: 4 } }}>
        <Paper sx={{ p: 3, mb: 3, textAlign: 'center', borderTop: `4px solid ${GREEN}` }}>
          <CheckCircleIcon sx={{ fontSize: 52, color: GREEN, mb: 1 }} />
          <Typography variant="h6" fontWeight={700} gutterBottom>Sale recorded</Typography>
          {booking && (
            <Typography variant="body2" color="text.secondary">
              {booking.client_name} · £{fmt(dueToday)} · {paymentMethod}
            </Typography>
          )}
        </Paper>

        {!rebookDone ? (
          <Paper sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <EventRepeatIcon sx={{ color: NAVY }} />
              <Typography variant="h6" fontWeight={700}>Rebook next appointment?</Typography>
            </Box>
            {recommendedWeeks && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                We recommend every <strong>{recommendedWeeks} weeks</strong> for{' '}
                {booking?.services?.[0]?.service_name || 'this service'}.
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
              {booking?.services?.length
                ? `${booking.services.map(s => s.service_name).join(' + ')} — select how many weeks ahead:`
                : 'Select how many weeks ahead:'}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 1 }}>
              {WEEK_OPTIONS.map(weeks => {
                const suggestedDate = booking ? addWeeks(booking.appointment_date, weeks) : null;
                const isRecommended = recommendedWeeks === weeks;
                const avail         = weekAvailability[weeks];
                return (
                  <Button key={weeks} variant={isRecommended ? 'contained' : 'outlined'}
                    onClick={() => handleWeekClick(weeks)} sx={weekButtonStyle(weeks)}>
                    {isRecommended && (
                      <Typography variant="caption" sx={{ fontSize: '0.6rem', fontWeight: 700, mb: 0.25, color: 'rgba(255,255,255,0.85)', letterSpacing: 0.5 }}>
                        SUGGESTED
                      </Typography>
                    )}
                    <Typography variant="h6" fontWeight={700} lineHeight={1}>{weeks}w</Typography>
                    {suggestedDate && (
                      <Typography variant="caption" sx={{ mt: 0.5, color: isRecommended ? 'rgba(255,255,255,0.75)' : 'text.secondary' }}>
                        {new Date(suggestedDate + 'T00:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                      </Typography>
                    )}
                    {avail === 'checking'    && <CircularProgress size={10} sx={{ mt: 0.5, color: isRecommended ? 'rgba(255,255,255,0.6)' : 'text.disabled' }} />}
                    {avail === 'unavailable' && <Typography variant="caption" sx={{ mt: 0.25, fontSize: '0.6rem', color: '#bdbdbd' }}>check diary</Typography>}
                    {avail === 'limited'     && <Typography variant="caption" sx={{ mt: 0.25, fontSize: '0.6rem', color: isRecommended ? 'rgba(255,255,255,0.75)' : '#BF360C' }}>limited slots</Typography>}
                  </Button>
                );
              })}
            </Box>
            <Typography variant="caption" color="text.secondary">
              Tap a week to confirm the date and time — or adjust before booking.
            </Typography>
          </Paper>
        ) : (
          <Paper sx={{ p: 3, mb: 3, borderTop: `4px solid ${NAVY}` }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <CalendarMonthIcon sx={{ color: NAVY }} />
              <Typography variant="h6" fontWeight={700}>Next appointment booked</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              {booking?.client_name} · {fmtDisplayDate(rebookDate)} · {booking?.stylist_name}
            </Typography>
          </Paper>
        )}

        <Button fullWidth variant="contained" size="large" onClick={() => navigate('/pro/diary')}
          sx={{ py: 1.5, bgcolor: NAVY, '&:hover': { bgcolor: '#1e2530' } }}>
          Done — back to diary
        </Button>

        <RebookDialog
          open={rebookOpen} onClose={() => setRebookOpen(false)}
          booking={booking} lineItems={lineItems}
          weeksAhead={rebookWeeks || 6} onBooked={handleRebookConfirmed}
        />
      </Box>
    );
  }

  // ── Main checkout view ────────────────────────────────────────────────
  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', p: { xs: 1.5, md: 3 } }}>

      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/pro/diary')} sx={{ mb: 2 }}>
        Back to Diary
      </Button>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Typography variant="h5" fontWeight={700} sx={{ mb: 2.5 }}>Checkout</Typography>

      <Grid container spacing={2} alignItems="flex-start">

        {/* LEFT: Services & Products */}
        <Grid item xs={12} md={7}>
          <Paper sx={{ p: 2.5 }}>

            {booking && (
              <Box sx={{ mb: 2.5, pb: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle1" fontWeight={600}>{booking.client_name}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {booking.client_phone}
                  {booking.stylist_name && ` · ${booking.stylist_name}`}
                  {booking.appointment_date && ` · ${booking.appointment_date}`}
                  {booking.start_time && ` at ${booking.start_time}`}
                </Typography>
              </Box>
            )}

            <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1 }}>
              Services &amp; Products
            </Typography>

            <Box sx={{ mt: 1, mb: 2 }}>
              {lineItems.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                  No services loaded.
                </Typography>
              )}
              {lineItems.map(item => (
                <Box key={item.id} sx={{
                  display: 'flex', alignItems: 'center', py: 1.5,
                  borderBottom: '1px solid', borderColor: 'divider', gap: 1,
                }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    {/* Name + type chip + price lock chip */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                      <Typography variant="body2" fontWeight={500} sx={{ wordBreak: 'break-word' }}>
                        {item.name}
                      </Typography>
                      <Chip label={item.type} size="small"
                        color={item.type === 'service' ? 'primary' : 'secondary'}
                        sx={{ height: 18, fontSize: '10px' }} />
                      {item.price_locked && item.locked_price != null && item.current_price != null &&
                        Math.abs(parseFloat(item.locked_price) - parseFloat(item.current_price)) > 0.005 && (
                        <Tooltip title={`Booked at £${parseFloat(item.locked_price).toFixed(2)} — the current price is £${parseFloat(item.current_price).toFixed(2)}`}>
                          <Chip
                            icon={<LockIcon sx={{ fontSize: '10px !important' }} />}
                            label="Booked price"
                            size="small"
                            sx={{
                              height: 18, fontSize: '10px',
                              backgroundColor: '#e8f5e9', color: '#2e7d32',
                              '& .MuiChip-icon': { color: '#2e7d32' },
                            }}
                          />
                        </Tooltip>
                      )}
                    </Box>
                    {item.duration > 0 && (
                      <Typography variant="caption" color="text.secondary">{item.duration} min</Typography>
                    )}
                  </Box>
                  <TextField
                    value={item.price}
                    onChange={e => updatePrice(item.id, e.target.value)}
                    size="small" type="number" variant="standard"
                    inputProps={{ min: 0, step: 0.01, style: { textAlign: 'right', width: 68, fontSize: 14 } }}
                    InputProps={{
                      startAdornment: <Typography variant="body2" sx={{ mr: 0.25, color: 'text.secondary' }}>£</Typography>,
                    }}
                  />
                  <IconButton size="small" onClick={() => removeItem(item.id)}
                    sx={{ color: 'text.disabled', '&:hover': { color: 'error.main' } }}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
            </Box>

            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <TextField select value={selectedRetailId} onChange={e => setSelectedRetailId(e.target.value)}
                size="small" label="Add retail product..." sx={{ flex: 1 }}>
                <MenuItem value=""><em>Select product</em></MenuItem>
                {retailProducts.map(p => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.name} — £{fmt(p.retail_price || p.price)}
                  </MenuItem>
                ))}
              </TextField>
              <Button variant="outlined" startIcon={<AddIcon />} onClick={addRetailProduct}
                disabled={!selectedRetailId} size="small" sx={{ whiteSpace: 'nowrap' }}>
                + Add
              </Button>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">Subtotal</Typography>
                <Typography variant="body2">£{fmt(subtotal)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">Discount (£)</Typography>
                <TextField value={discount} onChange={e => setDiscount(e.target.value)}
                  size="small" type="number" variant="standard"
                  inputProps={{ min: 0, step: 0.01, style: { textAlign: 'right', width: 80, fontSize: 14 } }} />
              </Box>
              {vatRegistered && dueToday > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  Includes £{fmt(vatAmount)} VAT collected for HMRC
                </Typography>
              )}
            </Box>
          </Paper>
        </Grid>

        {/* RIGHT: Payment */}
        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 2.5 }}>
            <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1 }}>
              Payment
            </Typography>

            <Box sx={{ display: 'flex', gap: 1, mt: 1, mb: 2 }}>
              {['Cash', 'Card', 'Split'].map(method => (
                <Button key={method} variant={paymentMethod === method ? 'contained' : 'outlined'}
                  onClick={() => setPaymentMethod(method)} size="small" sx={{
                    flex: 1,
                    ...(paymentMethod === method && {
                      bgcolor: NAVY, borderColor: NAVY,
                      '&:hover': { bgcolor: '#1e2530', borderColor: '#1e2530' },
                    }),
                  }}>
                  {method}
                </Button>
              ))}
            </Box>

            {paymentMethod === 'Split' && (
              <Box sx={{ mb: 2 }}>
                {splitRows.map((row, idx) => (
                  <Box key={idx} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                    <TextField select value={row.method} onChange={e => updateSplitRow(idx, 'method', e.target.value)}
                      size="small" sx={{ flex: 1 }}>
                      {['Cash', 'Card', 'Bank Transfer', 'Voucher'].map(m => (
                        <MenuItem key={m} value={m}>{m}</MenuItem>
                      ))}
                    </TextField>
                    <TextField value={row.amount} onChange={e => updateSplitRow(idx, 'amount', e.target.value)}
                      size="small" type="number" label="£"
                      inputProps={{ min: 0, step: 0.01 }} sx={{ width: 88 }} />
                    {splitRows.length > 1 && (
                      <IconButton size="small" onClick={() => removeSplitRow(idx)} color="error">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                ))}
                <Button size="small" startIcon={<AddIcon />} onClick={addSplitRow} sx={{ mb: 1 }}>
                  Add row
                </Button>
                {splitRemaining > 0.01 && (
                  <Alert severity="warning" sx={{ py: 0.5, fontSize: '12px' }}>
                    £{fmt(splitRemaining)} still unallocated
                  </Alert>
                )}
              </Box>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="body2" color="text.secondary">Tip (£)</Typography>
              <TextField value={tip} onChange={e => setTip(e.target.value)}
                size="small" type="number" variant="standard"
                inputProps={{ min: 0, step: 0.01, style: { textAlign: 'right', width: 80, fontSize: 14 } }} />
            </Box>

            <Box sx={{
              bgcolor: NAVY, color: 'white', borderRadius: 1.5,
              px: 2.5, py: 1.75, display: 'flex',
              justifyContent: 'space-between', alignItems: 'center', mb: 2,
            }}>
              <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.75)' }}>Total due</Typography>
              <Typography variant="h4" fontWeight={700}>£{fmt(dueToday)}</Typography>
            </Box>

            <Button fullWidth variant="contained"
              startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <CheckIcon />}
              onClick={handleCompleteSale} disabled={saving}
              sx={{ mb: 1.5, py: 1.5, fontSize: '15px', bgcolor: NAVY, '&:hover': { bgcolor: '#1e2530' } }}>
              Complete &amp; record sale
            </Button>

            <Button fullWidth variant="outlined" color="inherit"
              startIcon={<BlockIcon />} onClick={handleNoShow} disabled={saving}
              sx={{ color: 'text.secondary', borderColor: 'divider' }}>
              Mark as no-show
            </Button>
          </Paper>
        </Grid>
      </Grid>

      <Snackbar open={snackbar.open} autoHideDuration={4000}
        onClose={() => setSnackbar(p => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}